import React, { useCallback, useEffect, useState } from 'react';
import { Breadcrumb, Card, Col, Row, Badge, Spinner, Button, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/approval-queue.css';

const formatUser = (user) => {
    if (!user) return '—';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User #${user.user_id}`;
};

const formatRequestType = (type) =>
    (type || 'request').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ApprovalQueue = () => {
    const { accessToken, userId } = useAuth();
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState('pending');
    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [decisionReason, setDecisionReason] = useState({});
    const [actingId, setActingId] = useState(null);

    const loadApprovals = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${config.apiBASEURL}/TaskBoardRoutes/approvals`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { status: statusFilter },
            });
            setApprovals(response.data?.approvals || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load approval queue');
            setApprovals([]);
        } finally {
            setLoading(false);
        }
    }, [accessToken, statusFilter]);

    useEffect(() => {
        loadApprovals();
    }, [loadApprovals]);

    const handleDecide = async (approvalId, decision) => {
        if (!accessToken) return;
        setActingId(approvalId);
        setError('');
        setSuccessMessage('');
        try {
            const res = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/approvals/${approvalId}/decide`,
                {
                    decision,
                    decision_reason: decisionReason[approvalId] || '',
                    user_id: userId,
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const applied = res.data?.applied;
            const requestType = res.data?.approval?.request_type;
            if (decision === 'approve') {
                if (requestType === 'schedule_shift' && applied?.changes_applied === 0) {
                    setError(
                        'Approval recorded but no due dates were updated. The request may have been missing schedule data — submit a new approval or use Apply on Project Timeline.'
                    );
                } else if (
                    requestType === 'assignment_conflict' &&
                    !applied?.user_ids?.length
                ) {
                    setError(
                        'Approval recorded but card members were not updated. Ask the requester to submit a new approval with the selected members.'
                    );
                } else if (requestType === 'assignment_conflict' && applied?.user_ids?.length) {
                    setSuccessMessage(
                        `Assignment applied — ${applied.user_ids.length} member(s) updated on the card.`
                    );
                }
            }
            await loadApprovals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update approval');
        } finally {
            setActingId(null);
        }
    };

    return (
        <Row className="body_content approval-queue-page">
            <Col lg={12} className="mb-3">
                <Breadcrumb>
                    <Breadcrumb.Item
                        onClick={() => navigate('/Graphe/dashboard/boards_list')}
                        style={{ cursor: 'pointer' }}
                    >
                        Boards
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>Schedule approvals</Breadcrumb.Item>
                </Breadcrumb>
            </Col>

            <Col lg={12}>
                <Card>
                    <Card.Body>
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                            <div>
                                <h4 className="mb-1">Schedule approval queue</h4>
                                <p className="text-muted small mb-0">
                                    Review overtime, deadline extensions, and assignment conflicts.
                                </p>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                {!loading && (
                                    <Badge bg="secondary" className="py-2 px-3">
                                        {approvals.length} request{approvals.length === 1 ? '' : 's'}
                                    </Badge>
                                )}
                                <Form.Select
                                    style={{ minWidth: 160 }}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="all">All</option>
                                </Form.Select>
                            </div>
                        </div>

                        {error && <Alert variant="danger">{error}</Alert>}
                        {successMessage && <Alert variant="success">{successMessage}</Alert>}

                        {loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading approvals…
                            </div>
                        )}

                        {!loading && approvals.length === 0 && (
                            <div className="approval-queue-empty">
                                <p className="mb-0">
                                    No {statusFilter === 'all' ? '' : statusFilter} approval requests.
                                </p>
                            </div>
                        )}

                        {!loading && approvals.length > 0 && (
                            <div className="approval-queue-list">
                                {approvals.map((item) => (
                                    <article
                                        key={item.id}
                                        className={`approval-queue-item is-${item.status || 'pending'}`}
                                    >
                                        <div className="approval-queue-item-header">
                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                                <Badge bg="dark" className="approval-queue-type-badge">
                                                    {formatRequestType(item.request_type)}
                                                </Badge>
                                                <Badge
                                                    bg={
                                                        item.status === 'pending'
                                                            ? 'warning'
                                                            : item.status === 'approved'
                                                              ? 'success'
                                                              : 'secondary'
                                                    }
                                                    text={item.status === 'pending' ? 'dark' : undefined}
                                                >
                                                    {item.status}
                                                </Badge>
                                            </div>
                                            <small className="text-muted">
                                                {new Date(item.created_at).toLocaleString()}
                                            </small>
                                        </div>

                                        <dl className="approval-queue-meta">
                                            {item.card && (
                                                <div>
                                                    <dt>Card</dt>
                                                    <dd>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0 align-baseline"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/Graphe/dashboard/kanbanboard/${item.card.board_id}?card=${item.card.card_id}`
                                                                )
                                                            }
                                                        >
                                                            {item.card.title}
                                                        </button>
                                                    </dd>
                                                </div>
                                            )}
                                            {item.project && (
                                                <div>
                                                    <dt>Project</dt>
                                                    <dd>
                                                        {item.project.project_name}
                                                        {item.project.execution_priority && (
                                                            <Badge
                                                                bg="light"
                                                                text="dark"
                                                                className="ms-2"
                                                            >
                                                                {item.project.execution_priority}
                                                            </Badge>
                                                        )}
                                                    </dd>
                                                </div>
                                            )}
                                            <div>
                                                <dt>Assignee</dt>
                                                <dd>{formatUser(item.user)}</dd>
                                            </div>
                                            <div>
                                                <dt>Requested by</dt>
                                                <dd>{formatUser(item.requester)}</dd>
                                            </div>
                                        </dl>

                                        {item.request_type === 'assignment_conflict' &&
                                            item.payload?.user_ids?.length > 0 && (
                                                <p className="mb-2 small">
                                                    <strong>Requested assignment:</strong>{' '}
                                                    {item.payload.member_names ||
                                                        (item.payload.members || [])
                                                            .map((member) =>
                                                                `${member.first_name || ''} ${member.last_name || ''}`.trim()
                                                            )
                                                            .filter(Boolean)
                                                            .join(', ') ||
                                                        `${item.payload.user_ids.length} member${
                                                            item.payload.user_ids.length === 1 ? '' : 's'
                                                        }`}
                                                </p>
                                            )}

                                        {item.reason && (
                                            <div className="approval-queue-reason">{item.reason}</div>
                                        )}

                                        {item.status === 'pending' && (
                                            <div className="approval-queue-actions">
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Decision note (optional)"
                                                    value={decisionReason[item.id] || ''}
                                                    onChange={(e) =>
                                                        setDecisionReason((prev) => ({
                                                            ...prev,
                                                            [item.id]: e.target.value,
                                                        }))
                                                    }
                                                />
                                                <div className="approval-queue-buttons">
                                                    <Button
                                                        variant="success"
                                                        disabled={actingId === item.id}
                                                        onClick={() => handleDecide(item.id, 'approve')}
                                                    >
                                                        {actingId === item.id ? 'Saving…' : 'Approve'}
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        disabled={actingId === item.id}
                                                        onClick={() => handleDecide(item.id, 'reject')}
                                                    >
                                                        Reject
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {item.decision_reason && (
                                            <p className="mb-0 mt-3 small text-muted">
                                                <strong>Decision note:</strong> {item.decision_reason}
                                            </p>
                                        )}
                                    </article>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default ApprovalQueue;
